import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Board, ConnectionRow } from '../api/client.js';
import { fetchProjectBoards, fetchProjectConnections } from '../api/client.js';
import { useDictionary } from '../i18n/context.js';
import type { Dictionary } from '../i18n/types.js';

// ---------------------------------------------------------------------------
// A minimal project screen: identity and account connection health for both
// platforms. Project Overview's full content order (UI structure
// specification section 13.3 -- needs attention, seven-day schedule
// summary, active campaigns) is out of Phase 1's shipped surface (see the
// task-19 report); this route exists so "Open" from Home's project table
// and the OAuth callback's return path (apps/worker/src/oauth/{x,pinterest}.ts's
// `defaultReturnPath`) land somewhere real.
// ---------------------------------------------------------------------------

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; connections: ConnectionRow[] };

type BoardsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; boards: Board[] }
  | { status: 'error' };

// ---------------------------------------------------------------------------
// A connection's own `state` -- not merely whether a row exists at all --
// decides what the panel shows. `renewal_required`/`revoked`/`error` all
// mean the same thing to an operator here: something needs their attention
// before this destination can be trusted again, distinct from both a
// healthy connection and one that was never made.
//
// Task 6 review Critical 1: this used to be inferred from whether
// GET /api/projects/:id/boards happened to succeed (404 -> not connected,
// otherwise connected). That conflated the connection's own health with a
// live external call's success right now -- a `revoked`/`error` Pinterest
// connection still passes boards.ts's own connection-exists check and only
// fails on the live Pinterest call, so it rendered as "Connected" anyway,
// hiding a broken destination until a campaign attached to it failed to
// publish. Reading `state` from GET /api/projects/:id/connections (which
// now reports every platform, not only X) is the direct fix. Exported so
// this decision can be unit-tested without rendering the route or mocking
// routing/i18n context.
// ---------------------------------------------------------------------------

export type ConnectionView =
  | { status: 'not_connected' }
  | { status: 'needs_reconnect' }
  | { status: 'connected'; accountHandle: string };

export const connectionView = (connections: ConnectionRow[], platform: ConnectionRow['platform']): ConnectionView => {
  const connection = connections.find((c) => c.platform === platform) ?? null;
  if (!connection) return { status: 'not_connected' };
  if (connection.state !== 'connected') return { status: 'needs_reconnect' };
  return { status: 'connected', accountHandle: connection.accountHandle };
};

const ConnectionStatus = ({
  dict, view, startHref, connectLabel,
}: {
  dict: Dictionary; view: ConnectionView; startHref: string; connectLabel: string;
}): React.JSX.Element => {
  if (view.status === 'connected') {
    return (
      <>
        <span className="chip chip--done">{dict.ui.project.connected}</span>
        <span className="mono-meta">@{view.accountHandle}</span>
      </>
    );
  }
  if (view.status === 'needs_reconnect') {
    return (
      <>
        <span className="chip chip--danger">{dict.attentionReasons.reconnectionRequired}</span>
        <a className="btn btn--primary" href={startHref}>{dict.actions.reconnect}</a>
      </>
    );
  }
  return (
    <>
      <span className="chip">{dict.ui.project.notConnected}</span>
      <a className="btn btn--primary" href={startHref}>{connectLabel}</a>
    </>
  );
};

export const ProjectRoute = (): React.JSX.Element => {
  const dict = useDictionary();
  const { projectId } = useParams<{ projectId: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [boardsState, setBoardsState] = useState<BoardsState>({ status: 'idle' });

  useEffect(() => {
    if (!projectId) return;
    setState({ status: 'loading' });
    fetchProjectConnections(projectId)
      .then((res) => setState({ status: 'ready', connections: res.connections }))
      .catch(() => setState({ status: 'error' }));
  }, [projectId]);

  // The board list is a wholly separate concern from connection health: it
  // may be loading, empty, or failed without ever changing the connection
  // chip above (UI structure specification section 13). It is only worth
  // fetching once the connection itself is known healthy.
  useEffect(() => {
    if (!projectId || state.status !== 'ready') return;
    if (connectionView(state.connections, 'pinterest').status !== 'connected') {
      setBoardsState({ status: 'idle' });
      return;
    }
    setBoardsState({ status: 'loading' });
    fetchProjectBoards(projectId)
      .then((res) => setBoardsState({ status: 'ready', boards: res.boards }))
      .catch(() => setBoardsState({ status: 'error' }));
  }, [projectId, state]);

  if (!projectId) return <p className="t-body">{dict.ui.project.notFound}</p>;

  const xView = state.status === 'ready' ? connectionView(state.connections, 'x') : null;
  const pinterestView = state.status === 'ready' ? connectionView(state.connections, 'pinterest') : null;

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <Link className="btn btn--ghost" to="/home">{dict.ui.project.backToHome}</Link>
      <div className="between">
        <h1 className="t-title">{dict.ui.project.title}</h1>
        {/* Review fix (Important 1): the brand-context screen
            (/projects/:projectId/brand-context) had no way in from the
            product -- App.tsx registers the route, but nothing linked to
            it, so the only way an operator could reach it was typing the
            URL by hand. UI structure spec 4.2 puts project-scoped
            navigation on the project screen itself, which is where an
            operator already is when they want to edit brand voice. */}
        <Link className="btn btn--ghost" to={`/projects/${projectId}/brand-context`}>
          {dict.ui.brandContext.title}
        </Link>
      </div>

      {state.status === 'loading' && <p className="t-meta">{dict.ui.project.loading}</p>}

      {xView && (
        <div className="panel">
          <div className="panel__head"><h2 className="t-section">{dict.nav.aiConnections} — X</h2></div>
          <div className="panel__body row wrap" style={{ gap: 'var(--sp-3)' }}>
            <ConnectionStatus
              dict={dict} view={xView}
              startHref={`/api/projects/${projectId}/connections/x/start`}
              connectLabel={dict.ui.project.connectXAccount}
            />
          </div>
        </div>
      )}

      {pinterestView && (
        <div className="panel">
          <div className="panel__head"><h2 className="t-section">{dict.nav.aiConnections} — Pinterest</h2></div>
          <div className="panel__body stack-2">
            <div className="row wrap" style={{ gap: 'var(--sp-3)' }}>
              <ConnectionStatus
                dict={dict} view={pinterestView}
                startHref={`/api/projects/${projectId}/connections/pinterest/start`}
                connectLabel={dict.ui.project.connectPinterestAccount}
              />
            </div>

            {/* Board selection: shown once the connection is healthy, even
                while (or if) the live board list itself is still loading or
                failed to load -- a pending or unavailable list blocks only
                this control, never the connection chip above. */}
            {pinterestView.status === 'connected' && (
              <div className="field">
                <span className="field__label">{dict.ui.project.boards}</span>
                {boardsState.status === 'ready'
                  ? (
                    <select className="select" disabled={boardsState.boards.length === 0}>
                      {boardsState.boards.map((board) => (
                        <option key={board.id} value={board.id}>{board.name}</option>
                      ))}
                    </select>
                  )
                  : <select className="select" disabled />}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
